<script lang="ts">
    import { sliderDirective } from "@utils/directives/accessibility"
    import { onMount } from "svelte"
    import { createEventDispatcher } from "svelte"
    import { derived, writable } from "svelte/store"

    const dispatch = createEventDispatcher()

    export let gradient: string | null = null //= "90deg, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%"
    export let rainbow = false
    export let percentage: number
    export let min = 0
    export let max = 1
    export let label = ""

    export let small = false // this is for font size editor

    const difference = max - min

    let dragger: HTMLElement
    let wrapper: HTMLElement

    $: {
        if (wrapperWidth) {
            percentage
            moveHandler()
        }
    }
    let left: number = 0
    let actualPercentage = 0
    function moveHandler() {
        // Conver to pixels
        if (!wrapper) return
        actualPercentage = (percentage - min) / difference
        left = wrapper.getBoundingClientRect().width * actualPercentage
    }

    onMount(() => moveHandler())

    let initialDelta = 0
    let dragging = false

    function backPointerStart(e: PointerEvent) {
        dragging = true
        initialDelta = 0
        pointerMove(e)
    }

    function pointerStart(e: PointerEvent) {
        e.stopPropagation()
        initialDelta = dragger.getBoundingClientRect().left - e.clientX
        dragging = true
    }

    function pointerMove(e: PointerEvent) {
        if (!dragging) return
        const normalizedTouch =
            (e.clientX - wrapper.getBoundingClientRect().left + initialDelta) /
            wrapper.getBoundingClientRect().width
        const constrained = Math.max(Math.min(1, normalizedTouch), 0)
        percentage = constrained * difference + min
    }

    function pointerEnd(e: PointerEvent) {
        if (!dragging) return
        e.stopPropagation()
        dispatch("done", percentage)
        dragging = false
    }

    function touchmove(e: TouchEvent) {
        if (dragging) e.preventDefault()
    }

    // This is for accessbility
    const valuestore = writable(percentage)
    $: $valuestore = percentage

    let wrapperWidth: number = 0
</script>

<svelte:window
    on:pointermove={pointerMove}
    on:pointerup={pointerEnd}
    on:pointercancel={pointerEnd}
/>
<div
    tabindex={0}
    aria-label={label}
    role="slider"
    aria-valuemin={min}
    aria-valuemax={max}
    aria-valuenow={percentage}
    use:sliderDirective={{
        min,
        max,
        valuestore,
        callback: (p) => (percentage = p),
    }}
    class:small
    on:pointerdown={backPointerStart}
    class="wrapper"
    class:mouseout={!dragging}
    style:background={gradient || rainbow
        ? "linear-gradient(" + (gradient || rainbow) + ")"
        : `linear-gradient(90deg, #444 ${actualPercentage * 100}%, #bbb ${
              actualPercentage * 100
          }%)`}
    bind:this={wrapper}
    bind:clientWidth={wrapperWidth}
>
    <div
        class="slider"
        style:left={left + "px"}
        bind:this={dragger}
        on:pointerdown={pointerStart}
        on:touchmove={touchmove}
    >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
            <g
                stroke-width="2.5"
                fill="none"
                stroke="currentColor"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <line x1="1.5" y1="2.5" x2="15.5" y2="2.5" />
                <line x1="1.5" y1="8.5" x2="15.5" y2="8.5" />
                <line x1="1.5" y1="14.5" x2="15.5" y2="14.5" />
            </g>
        </svg>
    </div>
</div>

<style>
    .wrapper {
        height: 2em;
        margin: 1em;
        position: relative;
        border-radius: 0.4em;
        width: calc(100% - 2em);
    }
    .wrapper.small {
        height: 0.3em;
    }
    .wrapper.small .slider {
        height: 2em;
    }
    .wrapper.small .slider svg {
        display: block;
        top: 0.2em;
        left: 0.05em;
        color: black;
        opacity: 0.2;
        height: 50%;
        z-index: 2;
        position: absolute;
        transform: translate(-50%, -50%) rotate(90deg) scale(0.8);
    }
    .wrapper.small .slider:after {
        left: -1em;
        right: -1em;
        top: -0.85em;
        bottom: unset;
        height: 2em;
        border-radius: 50%;
    }
    .wrapper.mouseout {
        transition: background 0.4s ease-in-out;
    }
    .wrapper.mouseout .slider {
        transition: left 0.2s ease-in-out;
    }
    .wrapper .slider {
        background-color: rgba(255, 0, 0, 0.3333333333);
        width: 0;
        position: absolute;
        height: 2em;
    }
    .wrapper .slider svg {
        display: none;
    }
    .wrapper .slider:after {
        position: absolute;
        background-color: #f8f8f8;
        box-shadow: none;
        content: "";
        display: block;
        border-radius: 0.4em;
        left: -1em;
        right: -1em;
        top: -0.5em;
        bottom: -0.5em;
    }
</style>
